import React, { useState } from "react";
import {
  EmailIcon,
  EmailShareButton,
  FacebookIcon,
  FacebookShareButton,
  TwitterShareButton
} from "react-share";
import {
  MdShare as ShareIcon,
  MdContentCopy as CopyIcon,
  MdLink as LinkIcon
} from "react-icons/md";
import useClipboard from "react-use-clipboard";

import { CopyButton, MobileShareButton } from "./styles";
import { cordovaShare } from "../../../utils/cordovaUtils";
import { TwitterIconSmall } from "icons/TwitterIcon";

interface SocialMessage {
  content: string;
  reference: string;
}

interface SocialButtonProps {
  message: SocialMessage;
  url: string;
  socialType: string;
}

const getMessage = (message: SocialMessage, maxContentLength?: number) => {
  if (!message) return "";
  const { content, reference } = message;
  const maxRefLength =
    typeof maxContentLength === "number" ? maxContentLength - 3 : Infinity; // 3 is a magic number that existed before refactor
  const isShortEnough =
    !maxContentLength || content.length < maxContentLength - reference.length;
  return (isShortEnough
    ? `${content} - ${reference}`
    : `${content.substring(
        0,
        maxRefLength - reference.length
      )}...\n - ${reference}`
  ).trim();
};

interface CordovaSocialButtonProps {
  message: SocialMessage;
  url: string;
}

const CordovaSocialButton: React.FC<CordovaSocialButtonProps> = ({
  message,
  url
}) => {
  const [sharedSuccess, setSharedSuccess] = useState(false);
  const [sharedFail, setSharedFailed] = useState(true);

  return (
    <MobileShareButton
      error={sharedFail}
      success={sharedSuccess}
      onClick={() =>
        cordovaShare(getMessage(message, 276), url)
          .then(success => {
            setSharedSuccess(success);
            setSharedFailed(false);
          })
          .catch(err => {
            console.error(err);
            setSharedSuccess(false);
            setSharedFailed(true);
          })
      }
    >
      <ShareIcon size={30} />
    </MobileShareButton>
  );
};

interface CopySocialButtonProps {
  copyData: string;
}

const CopySocialButton: React.FC<CopySocialButtonProps> = ({
  copyData,
  children
}) => {
  const [isCopied, setCopied] = useClipboard(copyData, {
    successDuration: 500
  });

  return (
    <CopyButton role="button" copied={isCopied} onClick={setCopied}>
      {children}
    </CopyButton>
  );
};

const SocialButton: React.FC<SocialButtonProps> = ({
  message,
  url = "",
  socialType
}) => {
  switch (socialType.toLowerCase()) {
    case "cordova":
      return <CordovaSocialButton message={message} url={url} />;
    case "facebook":
      return (
        <FacebookShareButton url={url} quote={getMessage(message, 60000)}>
          <FacebookIcon size={34} iconBgStyle={{ fill: "transparent" }} />
        </FacebookShareButton>
      );
    case "twitter":
      return (
        <TwitterShareButton url={url} title={getMessage(message, 276)}>
          <TwitterIconSmall size={16} style={{paddingLeft: 6}}/>
        </TwitterShareButton>
      );
    case "email":
      return (
        <EmailShareButton url={url} subject="" body={getMessage(message, 1296)}>
          <EmailIcon size={34} iconBgStyle={{ fill: "transparent" }} />
        </EmailShareButton>
      );
    case "link-copy":
      return (
        <CopySocialButton copyData={url}>
          <LinkIcon size={22} />
        </CopySocialButton>
      );
    case "verse-copy":
      return (
        <CopySocialButton copyData={getMessage(message)}>
          <CopyIcon size={22} />
        </CopySocialButton>
      );
    default:
      return null;
  }
};

export default SocialButton;
